<template>
  <div class="organization-management-container">
    <a-card title="组织管理" :bordered="false">
      <div slot="extra">
        <a-button type="primary" @click="openCreateDialog">新增组织</a-button>
        <a-button style="margin-left: 10px;" @click="loadOrganizations">刷新</a-button>
      </div>
      
      <!-- Organization Profile Drawer -->
      <organization-profile-drawer
        :visible.sync="profileDrawerVisible"
        :organization="currentProfileOrg"
        @close="handleProfileDrawerClose"
        @refresh="loadOrganizations"
      />

      <a-form layout="inline" :form="searchFormRef">
        <a-form-item label="组织名称">
          <a-input v-model="searchForm.name" placeholder="请输入组织名称" allowClear />
        </a-form-item>
        <a-form-item>
          <a-button type="primary" @click="loadOrganizations">查询</a-button>
          <a-button style="margin-left: 8px;" @click="resetSearch">重置</a-button>
        </a-form-item>
      </a-form>

      <div style="margin-top: 20px;">
        <a-table
          :columns="columns"
          :data-source="organizations"
          :loading="loading"
          :pagination="pagination"
          rowKey="id"
          style="width: 100%"
          @change="handleTableChange">
          <span slot="status" slot-scope="status, record">
            <a-tag :color="status === 2 ? 'green' : 'orange'">
              {{ record.status_text || (status === 2 ? '正常' : '未订阅') }}
            </a-tag>
          </span>
          <span slot="subscription_expired_at" slot-scope="text">
            {{ formatDateOnly(text) }}
          </span>
          <span slot="created_at" slot-scope="text">
            {{ formatDate(text) }}
          </span>
          <span slot="actions" slot-scope="text, record">
            <a-dropdown>
              <a class="ant-dropdown-link" @click="e => e.preventDefault()">
                操作 <a-icon type="down" />
              </a>
              <a-menu slot="overlay">
                <a-menu-item key="profile" @click="openProfileDrawer(record)">
                  <a-icon type="profile" />查看档案
                </a-menu-item>
                <a-menu-item key="edit" @click="openEditDialog(record)">
                  <a-icon type="edit" />编辑
                </a-menu-item>
                <a-menu-item key="sync-schema">
                  <a-popconfirm
                    title="确定要同步表结构吗？此操作可能会修改数据库表结构，导致数据丢失或损坏，请确保已备份数据！"
                    @confirm="syncTenantSchema(record)"
                    okText="确认同步"
                    cancelText="取消"
                    okType="danger"
                  >
                    <a-icon type="sync" />同步表结构
                  </a-popconfirm>
                </a-menu-item>
                <a-menu-item key="delete">
                  <a-popconfirm
                    title="确定要删除这个组织吗？此操作不可恢复"
                    @confirm="deleteOrganization(record)"
                    okText="确定"
                    cancelText="取消"
                  >
                    <a-icon type="delete" />删除
                  </a-popconfirm>
                </a-menu-item>
              </a-menu>
            </a-dropdown>
          </span>
        </a-table>
      </div>

      <!-- 组织编辑对话框 -->
      <a-modal :title="dialogTitle" :visible="dialogVisible" @ok="saveOrganization" @cancel="closeDialog" width="500px">
        <a-form :form="orgForm" :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
          <a-form-item label="组织名称" :validate-status="getOrgFieldError('name') ? 'error' : ''" :help="getOrgFieldError('name') || ''">
            <a-input 
              v-decorator="[
                'name',
                {
                  rules: [
                    { required: true, message: '请输入组织名称' },
                    { min: 2, max: 50, message: '长度在 2 到 50 个字符' }
                  ]
                }
              ]"
              placeholder="请输入组织名称" />
          </a-form-item>
          
          <a-form-item label="状态">
            <a-radio-group v-decorator="['status', { initialValue: 1 }]">
              <a-radio :value="1">启用</a-radio>
              <a-radio :value="0">禁用</a-radio>
            </a-radio-group>
          </a-form-item>
        </a-form>
      </a-modal>

      <!-- 分配用户对话框 -->
      <a-modal 
        title="分配用户到组织" 
        :visible="assignUserDialogVisible" 
        @ok="assignUserToOrganization" 
        @cancel="() => { assignUserDialogVisible = false }" 
        width="600px">
        <a-form :form="assignUserForm" :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
          <a-form-item label="组织">
            <span>{{ currentOrganization.name }}</span>
          </a-form-item>
          
          <a-form-item label="选择用户" :validate-status="getAssignUserFieldError('user_id') ? 'error' : ''" :help="getAssignUserFieldError('user_id') || ''">
            <a-select 
              v-decorator="[
                'user_id',
                {
                  rules: [{ required: true, message: '请选择用户' }]
                }
              ]"
              placeholder="请选择要分配的用户"
              style="width: 100%;">
              <a-select-option
                v-for="user in availableUsers"
                :key="user.id"
                :value="user.id">
                {{ user.user_name }} ({{ user.mobile }})
              </a-select-option>
            </a-select>
          </a-form-item>
        </a-form>
      </a-modal>
    </a-card>
  </div>
</template>

<script>
import OrganizationProfileDrawer from './components/OrganizationProfileDrawer.vue'
import { dateFormat } from '@/utils/dateFormat'

export default {
  name: 'OrganizationManagement',
  components: {
    OrganizationProfileDrawer
  },
  data() {
    return {
      searchFormRef: this.$form.createForm(this),
      orgForm: this.$form.createForm(this),
      assignUserForm: this.$form.createForm(this),
      organizations: [],
      loading: false,
      searchForm: {
        name: ''
      },
      // Profile drawer
      profileDrawerVisible: false,
      currentProfileOrg: {},
      columns: [
        { title: 'ID', dataIndex: 'id', width: 80 },
        { title: '组织名称', dataIndex: 'name', width: 150 },
        { title: '数据库标识', dataIndex: 'db_identifier', width: 150 },
        { title: '状态', dataIndex: 'status', width: 100, scopedSlots: { customRender: 'status' } },
        { title: '订阅到期时间', dataIndex: 'subscription_expired_at', width: 170, scopedSlots: { customRender: 'subscription_expired_at' } },
        { title: '创建时间', dataIndex: 'created_at', width: 170, scopedSlots: { customRender: 'created_at' } },
        { title: '操作', width: 150, scopedSlots: { customRender: 'actions' } }
      ],
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showQuickJumper: true,
        pageSizeOptions: ['10', '20', '50', '100']
      },
      dialogVisible: false,
      dialogTitle: '',
      currentOrganization: {},
      assignUserDialogVisible: false,
      availableUsers: [] // 可分配的用户列表
    }
  },
  async mounted() {
    await this.loadOrganizations()
  },
  methods: {
    async loadOrganizations() {
      this.loading = true
      try {
        const params = {
          page_no: this.pagination.current,
          page_size: this.pagination.pageSize,
          ...this.searchForm
        }
        
        const response = await this.$api.get_tenants_list(params)
        console.log('组织列表API响应:', response)
        this.organizations = response.data || []
        // 由于API返回的page是空对象，使用数据长度作为总数
        this.pagination.total = this.organizations.length
        console.log('处理后的组织数据:', this.organizations)
      } catch (error) {
        console.error('加载组织数据失败:', error)
        this.$message.error('加载组织数据失败')
      } finally {
        this.loading = false
      }
    },
    
    handleTableChange(pagination) {
      this.pagination.current = pagination.current
      this.pagination.pageSize = pagination.pageSize
      this.loadOrganizations()
    },
    
    resetSearch() {
      this.searchForm = {
        name: ''
      }
      this.pagination.current = 1
      this.loadOrganizations()
    },
    
    openCreateDialog() {
      this.dialogTitle = '新增组织'
      this.currentOrganization = {}
      this.dialogVisible = true
      this.orgForm.resetFields()
    },
    
    openEditDialog(row) {
      console.log('打开编辑对话框，传入数据:', row)
      this.dialogTitle = '编辑组织'
      this.currentOrganization = { ...row }
      this.dialogVisible = true
      
      // 使用nextTick确保DOM更新后再设置表单值
      this.$nextTick(() => {
        console.log('设置表单值:', {
          name: row.name,
          status: row.status || 1
        })
        this.orgForm.setFieldsValue({
          name: row.name,
          status: row.status || 1
        })
      })
    },
    
    openProfileDrawer(record) {
      this.currentProfileOrg = { ...record }
      this.profileDrawerVisible = true
    },
    
    handleProfileDrawerClose() {
      this.profileDrawerVisible = false
      this.currentProfileOrg = {}
    },
    
    closeDialog() {
      this.dialogVisible = false
      this.currentOrganization = {}
      this.orgForm.resetFields()
    },
    
    getOrgFieldError(field) {
      const errors = this.orgForm.getFieldError(field)
      return errors ? errors[0] : ''
    },
    
    getAssignUserFieldError(field) {
      const errors = this.assignUserForm.getFieldError(field)
      return errors ? errors[0] : ''
    },
    
    async saveOrganization() {
      try {
        const values = await this.orgForm.validateFields()
        
        if (this.currentOrganization.id) {
          // 更新组织
          await this.$api.update_tenant(this.currentOrganization.id, {
            name: values.name,
            status: values.status
          })
          this.$message.success('更新组织成功')
        } else {
          // 创建组织 - 使用正确的API
          const response = await this.$api.create_tenant({
            name: values.name
          })
          
          if (response.code === 200) {
            this.$message.success('创建组织成功')
          } else {
            throw new Error(response.message || '创建组织失败')
          }
        }
        
        this.closeDialog()
        await this.loadOrganizations()
      } catch (error) {
        if (error.errorFields) {
          // 表单验证错误
          return
        }
        console.error('保存组织失败:', error)
        this.$message.error(error.message || '保存组织失败')
      }
    },
    
    async deleteOrganization(row) {
      try {
        this.$confirm({
          title: '确认删除',
          content: `确定要删除组织 "${row.name}" 吗？`,
          okText: '确定',
          cancelText: '取消',
          onOk: async () => {
            try {
              await this.$api.delete_tenant(row.id)
              this.$message.success('删除成功')
              await this.loadOrganizations()
            } catch (error) {
              console.error('删除组织失败:', error)
              this.$message.error('删除组织失败')
            }
          }
        })
      } catch (error) {
        console.error('删除组织失败:', error)
        this.$message.error('删除组织失败')
      }
    },
    
    async openAssignUserDialog(organization) {
      this.currentOrganization = organization
      this.assignUserDialogVisible = true
      this.assignUserForm.resetFields()
      
      // 加载可分配的用户列表
      try {
        const response = await this.$api.getTenantUsersList({ page_size: 1000 })
        this.availableUsers = response.data.list || []
      } catch (error) {
        console.error('加载用户列表失败:', error)
        this.$message.error('加载用户列表失败')
        this.availableUsers = []
      }
    },
    
    async assignUserToOrganization() {
      try {
        const values = await this.assignUserForm.validateFields()
        
        // 调用分配用户到组织的API
        await this.$api.assign_user_to_tenant({
          user_id: values.user_id,
          tenant_id: this.currentOrganization.id
        })
        
        this.$message.success('分配用户成功')
        this.assignUserDialogVisible = false
        await this.loadOrganizations() // 重新加载数据
      } catch (error) {
        if (error.errorFields) {
          // 表单验证错误
          return
        }
        console.error('分配用户失败:', error)
        this.$message.error(error.message || '分配用户失败')
      }
    },
    
    // Format date utility
    formatDate(date) {
      if (!date) return '-'
      const d = new Date(date)
      if (isNaN(d.getTime())) return '-'
      return dateFormat(d, 'yyyy-MM-dd HH:mm:ss')
    },

    // Format date only (no time) - for subscription expiration dates
    formatDateOnly(date) {
      if (!date) return '-'
      const d = new Date(date)
      if (isNaN(d.getTime())) return '-'
      return dateFormat(d, 'yyyy-MM-dd')
    },

    // 同步租户数据库表结构
    async syncTenantSchema(record) {
      try {
        this.$message.loading({ content: `正在同步 "${record.name}" 的表结构...`, key: 'syncSchema' })
        
        const response = await this.$api.sync_tenant_schema(record.id)
        
        if (response.code === 200) {
          this.$message.success({ content: response.message, key: 'syncSchema' })
        } else {
          this.$message.error({ 
            content: response.message + (response.data?.details ? `: ${response.data.details}` : ''), 
            key: 'syncSchema' 
          })
        }
      } catch (error) {
        console.error('同步表结构失败:', error)
        this.$message.error({ content: '同步表结构失败: ' + error.message, key: 'syncSchema' })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.organization-management-container {
  ::v-deep .ant-card {
    min-height: calc(100vh - 100px);
  }
  
  ::v-deep .ant-form-inline .ant-form-item {
    margin-bottom: 24px;
  }
  
  ::v-deep .ant-table {
    .ant-table-thead > tr > th {
      background-color: #fafafa;
      font-weight: 500;
    }
    
    .ant-table-tbody > tr:hover > td {
      background-color: #f5f5f5;
    }
  }
  
  ::v-deep .ant-pagination {
    .ant-pagination-total-text {
      line-height: 32px;
    }
  }
  
  // 状态标签样式
  ::v-deep .ant-tag {
    margin-right: 4px;
  }
  
  // 操作按钮间距
  ::v-deep .ant-btn {
    margin-right: 4px;
    
    &:last-child {
      margin-right: 0;
    }
  }
}
</style>